import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiCheck, FiInfo, FiLoader, FiX } from 'react-icons/fi';
import { cApplyVerify, cVerifyMe } from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import { BLUE } from '../../components/community/brand';

/**
 * **Verification** (“ផ្ទៀងផ្ទាត់”) — the member sees the conditions the platform
 * admin created (10 posts, 10 shares, 20 followers, profile / cover / bio /
 * completed form …), their own progress, and applies for the ✓ mark.
 *
 * The ✓ itself is the brand picture `public/verify/verify_check.png`, shown next
 * to the name everywhere (posts, comments, profiles, leaderboard).
 */
export default function VerifyPage() {
  const { t, lang } = useLanguage();
  const { user, openAuth } = useCommunity();
  const [data, setData] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    cVerifyMe()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, [user]);

  const apply = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await cApplyVerify(note);
      toast.success(t('verifyPending'));
      setNote('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };

  const status = data?.request?.status;
  const badge = `${process.env.PUBLIC_URL}/verify/verify_check.png`;
  const label = (c) => (lang === 'kh' ? (c.label_km || c.label) : (c.label || c.label_km));

  return (
    <div className="mx-auto max-w-2xl px-3 lg:px-4 py-4">
      <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-5 text-center">
        <img src={badge} alt={t('verifiedBadge')} className="w-16 h-16 mx-auto object-contain" />
        <h1 className="mt-2 text-lg font-extrabold">{t('verifyTitle')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('verifyIntro')}</p>

        {data && (
          <div className="mt-4">
            <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${data.percent}%`, background: BLUE }}
              />
            </div>
            <p className="mt-1.5 text-xs font-bold" style={{ color: BLUE }}>
              {t('verifyProgress').replace('{done}', data.done).replace('{total}', data.total)}
            </p>
          </div>
        )}
      </section>

      {!user ? (
        <button
          type="button"
          onClick={() => openAuth('signin', t('verifyNeedLogin'))}
          className="mt-3 w-full py-3 rounded-2xl text-white font-bold"
          style={{ background: BLUE }}
        >
          {t('verifyNeedLogin')}
        </button>
      ) : loading ? (
        <p className="mt-4 text-sm text-gray-400">{t('loading')}</p>
      ) : !data ? (
        <p className="mt-4 text-sm text-gray-400">{t('somethingWrong')}</p>
      ) : (
        <>
          {data.verified && (
            <p className="mt-3 flex items-center gap-2 text-sm font-bold text-emerald-600">
              <FiCheck className="w-4 h-4" /> {t('verifyApproved')}
            </p>
          )}

          <section className="mt-3 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
            <h2 className="font-extrabold text-sm mb-2">{t('verifyCondition')}</h2>
            <ul className="space-y-2">
              {data.items.map((c) => (
                <li key={c.id || c.key} className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${c.done ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>
                    {c.done ? <FiCheck className="w-3.5 h-3.5" /> : <FiX className="w-3.5 h-3.5" />}
                  </span>
                  <span className="min-w-0 flex-1 text-sm">{label(c)}</span>
                  <span className={`text-xs font-bold ${c.done ? 'text-emerald-600' : 'text-gray-400'}`}>
                    {c.threshold > 0
                      ? t('verifyValue').replace('{value}', c.value).replace('{need}', c.threshold)
                      : (c.done ? t('verifyDone') : t('verifyComplete'))}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {status === 'pending' ? (
            <p className="mt-3 flex items-center gap-2 text-sm font-bold text-amber-600">
              <FiInfo className="w-4 h-4" /> {t('verifyPending')}
            </p>
          ) : status === 'rejected' ? (
            <p className="mt-3 text-sm font-bold text-red-500">
              {t('verifyRejected')}
              {data.request?.review_note ? ` · ${data.request.review_note}` : ''}
            </p>
          ) : null}

          <form onSubmit={apply} className="mt-3 space-y-2">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder={t('verifyNote')}
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3.5 py-2.5 text-sm outline-none focus:border-gray-400"
            />
            <button
              type="submit"
              disabled={busy || !data.can_apply}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-bold disabled:opacity-50"
              style={{ background: BLUE }}
            >
              {busy && <FiLoader className="w-4 h-4 animate-spin" />}
              {data.ok ? t('verifySend') : t('verifyNotMet')}
            </button>
          </form>

          <p className="mt-3 text-[11px] text-gray-400">
            {t('verifyComplete')} ·{' '}
            <Link to="/me?tab=profile" className="font-bold hover:underline" style={{ color: BLUE }}>
              {t('profileNav')}
            </Link>
          </p>
        </>
      )}
    </div>
  );
}